import { Details, Product } from 'src/services/linx-api/dtos'
import { ProductApi } from 'src/types'
import { calculateInstallmentWithInterest } from 'src/util/calculateInstallmentWithInterest'
import { AVAILABLE } from 'src/util/Linx'
import {
  isValidColorBySlug,
  isValidVoltageBySlug,
} from 'src/util/productCombination'
import {
  getActiveSKUFromSearchProduct,
  getActiveSKUFromShowcaseProduct,
  getShowcaseProductCategoryId,
} from 'src/util/products'
import { ProductCombination, ProductProps } from './product-vertical'

export const convertArraysToStringsOrNumbers = (obj: Details) => {
  for (const key in obj) {
    if (Array.isArray(obj[key])) {
      const numericValue = obj[key].find((val: any) => typeof val === 'number')
      const isNotNumericAndDefined =
        numericValue === undefined && obj[key][0] !== undefined
      const floatValue = isNotNumericAndDefined
        ? parseFloat(obj[key][0].replace(',', '.'))
        : obj[key][0]

      obj[key] = numericValue ?? isNaN(floatValue) ? obj[key][0] : floatValue
    }
  }
  return obj
}

export const mapProductToAnalyticsProductLinx = (product: Product) => {
  const resultDetails = convertArraysToStringsOrNumbers(product.details)

  return {
    ...product,
    resultDetails,
    selectedSku: product.details.variacaoId?.[0],
    cId: getShowcaseProductCategoryId(product),
    specs: {
      cor: getProductColorsForGA(product),
    },
  }
}

export const mapProductToAnalyticsProduct = (product: ProductApi) => {
  return {
    ...product,
    selectedSku: Array.isArray(product.details.variacaoId)
      ? product.details.variacaoId[0]
      : product.details.variacaoId,
    cId: getShowcaseProductCategoryId(product),
    specs: {
      cor: getProductColorsForGA(product),
    },
  }
}

export const mapToItemProdutoVertical = (
  product: any,
  isMobile = true
): Record<string, any> => {
  const color = makeSlug(product, 'cor_id', 'Cor')
  const voltage = makeSlug(product, 'voltagem_id', 'Voltagem')
  const productUrl = getActiveSKUFromSearchProduct(product)?.url || product.url

  const validatedTotalParcelado = Array.isArray(product.details.totalParcelado)
    ? product.details.totalParcelado[1]
    : product.details.totalParcelado

  const installmentPriceWithInterest = calculateInstallmentWithInterest(
    product.installment.count,
    Number(product.details.numeroParcelasSemJuros),
    product.installment.price
  )

  return {
    categories: product.categories,
    color,
    colorsQuantity: getColorsQuantity(product.details),
    de: product.oldPrice,
    id: product.id,
    imageUrl: product.images.default,
    imageWidth: isMobile ? 120 : 300,
    installmentPriceWithInterest,
    installmentMaxQuantity: product?.installment?.count || 0,
    installmentMaxQuantityWithoutInterest:
      product?.details?.numeroParcelasSemJuros,
    isAvailable: product.status.toUpperCase() === AVAILABLE,
    percent: product.details?.percentualDesconto,
    por: +validatedTotalParcelado,
    price: product.price,
    promoEventImage: {
      desktop: product.images?.promoEventDesktop,
      mobile: product.images?.promoEventMobile,
    },
    productUrl,
    reviewQuantity: product.details.quantidadeAvaliacoes ?? 0,
    reviewRating: product.details.mediaAvaliacoes ?? 0,
    skus: product.skus,
    title: product.name,
    trackingUrl: product.trackingUrl || product.clickUrl || undefined,
    voltage,
  }
}

export const mapLinxProductApiToProductVerticalItem = (
  product: ProductApi
): ProductProps => {
  const color = makeSlug(product, 'cor_id', 'Cor')
  const voltage = makeSlug(product, 'voltagem_id', 'Voltagem')
  const productUrl =
    getActiveSKUFromShowcaseProduct(product)?.url || product.url

  const validatedTotalParcelado = Array.isArray(product.details.totalParcelado)
    ? product.details.totalParcelado[1]
    : product.details.totalParcelado

  const installmentPriceWithInterest = calculateInstallmentWithInterest(
    product.installment.count,
    Number(product.details.numeroParcelasSemJuros),
    product.installment.price
  )

  return {
    color,
    colorsQuantity: getColorsQuantity(product.details),
    de: product.oldPrice,
    id: +product.id,
    imageUrl: product.images.default,
    installmentPriceWithInterest,
    installmentMaxQuantity: product?.installment?.count,
    installmentMaxQuantityWithoutInterest:
      product?.details?.numeroParcelasSemJuros,
    isAvailable: product.status.toUpperCase() === AVAILABLE,
    percent: product.details.percentualDesconto,
    por: validatedTotalParcelado,
    price: product.price,
    promoEventImage: {
      desktop: product.images?.promoEventDesktop,
      mobile: product.images?.promoEventMobile,
    },
    productUrl,
    reviewQuantity: product.details.quantidadeAvaliacoes ?? 0,
    reviewRating: product.details.mediaAvaliacoes ?? 0,
    title: product.name,
    trackingUrl: product.trackingUrl,
    voltage,
  }
}

const makeSlug = (
  product: ProductApi,
  slugTarget: string,
  valueTarget: string
): ProductCombination => {
  return {
    slug: product.details[slugTarget]?.toString() || '',
    valor: product.details[valueTarget]?.toString() || '',
  }
}

const getColorsQuantity = (productDetails: any) => {
  if (Array.isArray(productDetails.Cores)) {
    return productDetails.Cores.length
  }
  return productDetails?.Cores?.split(',').length || 0
}

export const formatVoltage = (voltage: string, label = '-volts') => {
  const treatedVoltage = voltage.replace(/\D/g, '')

  if (treatedVoltage) return `${treatedVoltage}${label}`

  return voltage
}

export const colorAndVoltageStructure = (product: ProductProps): string => {
  return [
    isValidColorBySlug(product.color?.slug) ? product.color.valor : undefined,
    isValidVoltageBySlug(product.voltage?.slug)
      ? formatVoltage(product.voltage.valor, ' Volts')
      : undefined,
  ]
    .filter(Boolean)
    .join(' | ')
}

export const getLinxClickEventDataFromUrl = (
  url: string
): Record<string, any> => {
  const queryParams = url.split('?')[1].split('&')

  return queryParams.reduce((linxData, queryParam) => {
    const [key, value] = queryParam.split('=')
    return { ...linxData, [key]: value }
  }, {})
}

export const getProductColorsForGA = (product: ProductApi | Product) => {
  return (
    product.skus?.map((sku) => {
      if (sku.specs.cor) {
        const color = Array.isArray(sku.specs.cor)
          ? sku.specs.cor[0]
          : sku.specs.cor

        return [{ label: color }]
      }
      return []
    }) || []
  )
}
